import cls from 'classnames';
import NextImage, {
  ImageLoader,
  ImageProps as NextImageProps,
} from 'next/image';
import * as R from 'ramda';
import { FC, useEffect, useMemo, useState } from 'react';

import { customLoader as contentfulLoader } from '@/lib/utils';

import Skeleton from '../skeleton';
import styles from './styles';

type ImageProps = {
  src: string;
  fallbackSrc?: string;
  alt?: string;
  loadImmediately?: boolean;
  objectFit?: NextImageProps['objectFit'];
  objectPosition?: NextImageProps['objectPosition'];
  basicLoader?: boolean;
  loader?: ImageLoader;
  className?: string;
} & (
  | {
      width?: never;
      height?: never;
      useIntrinsicSize?: false;
    }
  | {
      width: number | string;
      height: number | string;
      useIntrinsicSize: true;
    }
);

type LayoutProps =
  | {
      width?: never;
      height?: never;
      layout: 'fill';
      objectFit?: NextImageProps['objectFit'];
      objectPosition?: NextImageProps['objectPosition'];
    }
  | {
      width: number | string;
      height: number | string;
      layout?: 'intrinsic';
    };

const defaultImg = '/ech-new/images/ech/apple-touch-icon.png';

const Image: FC<ImageProps> = ({
  src,
  fallbackSrc,
  alt,
  useIntrinsicSize = false,
  objectFit,
  width,
  height,
  loadImmediately = false,
  basicLoader = false,
  loader,
  className = '',
  objectPosition,
}) => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [storedSrc, setStoredSrc] = useState(src);
  const [useBasicLoader, setUseBasicLoader] = useState(basicLoader);

  const isSvgFile = useMemo(() => {
    // newsletter component returns object in src
    if (typeof storedSrc === 'object') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return storedSrc.src.toString().includes('.svg');
    }

    return storedSrc?.includes('.svg');
  }, [storedSrc]);

  const isDefaultLoader = isSvgFile || useBasicLoader;
  const predefinedLoader = isDefaultLoader ? R.prop('src') : contentfulLoader;

  const layoutProps: LayoutProps = useIntrinsicSize
    ? {
        layout: 'intrinsic',
        // type inference doesn't work somehow, so we have to define defaults
        // even though the width/height, can never be undefined if useIntrinsicSize is true
        width: width ?? 0,
        height: height ?? 0,
      }
    : {
        layout: 'fill',
        objectFit,
        objectPosition,
      };

  function handleError() {
    if (fallbackSrc && storedSrc !== fallbackSrc) {
      setStoredSrc(fallbackSrc);
      setUseBasicLoader(false);
      setHasLoaded(false);

      return;
    }

    setStoredSrc(defaultImg);
  }

  useEffect(() => {
    setStoredSrc(src);
    setUseBasicLoader(basicLoader);
    setHasLoaded(false);
  }, [src, basicLoader, fallbackSrc]);

  return (
    <div
      className={cls(
        styles.wrapper,
        layoutProps.layout === 'fill' && 'h-full',
        className,
      )}
    >
      {!hasLoaded && <Skeleton className={className} />}

      <NextImage
        onLoadingComplete={() => {
          setHasLoaded(true);
        }}
        loader={loader || predefinedLoader}
        unoptimized={useBasicLoader}
        src={storedSrc}
        {...layoutProps}
        alt={alt}
        loading={loadImmediately ? 'eager' : 'lazy'}
        lazyBoundary="50% 50% 50% 50%"
        onError={handleError}
        placeholder="blur"
        blurDataURL={defaultImg}
      />
    </div>
  );
};

export default Image;
